import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EmailIcon from "@mui/icons-material/Email";
import HelpOutlineIcon from "@mui/icons-material/Help";
import { toast } from "sonner";

export const Route = createFileRoute("/support")({
  component: SupportPage,
  head: () => ({
    meta: [
      { title: "Support — JobYoda" },
      { name: "description", content: "Get help with JobYoda — FAQs, contact, and support resources." },
      { property: "og:title", content: "Support — JobYoda" },
      { property: "og:description", content: "Get help with JobYoda." },
    ],
  }),
});

const FAQS = [
  {
    q: "How does the 1–10 match score work?",
    a: "We compare your verified profile to the JD across skills, experience, domain, and seniority. You get a per-dimension breakdown plus the gaps to close.",
  },
  {
    q: "Where is my data stored?",
    a: "Your profile, applications, and generated materials are stored securely in our cloud database. You can export or delete everything at any time.",
  },
  {
    q: "Does JobYoda invent experience?",
    a: "No. Tailored materials are grounded in your verified profile. We reorder and emphasize — we never fabricate.",
  },
  {
    q: "How do rejection emails work?",
    a: "Forward rejection emails to your unique JobYoda inbox. We extract the real reason and add the missing skill to your learning queue.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel from your account and keep access until the end of your billing period.",
  },
];

function SupportPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Support request from ${name || "JobYoda user"}`);
    const body = encodeURIComponent(`From: ${name} <${email}>\n\n${message}`);
    window.location.href = `mailto:support@jobyoda.com?subject=${subject}&body=${body}`;
    toast.success("Opening your email client…");
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
        <Button component={Link} to="/" size="small" color="inherit" sx={{ mb: 2 }}>
          ← Back to home
        </Button>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
          Support
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          We're here to help. Browse FAQs or send us a message.
        </Typography>

        {/* Quick contact cards */}
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, mb: 5 }}>
          <Card variant="outlined">
            <CardContent>
              <EmailIcon color="primary" />
              <Typography variant="h6" sx={{ mt: 1 }}>Email us</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Reach our team within 24 hours.
              </Typography>
              <Button href="mailto:support@jobyoda.com" variant="outlined" size="small">
                support@jobyoda.com
              </Button>
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent>
              <HelpOutlineIcon color="primary" />
              <Typography variant="h6" sx={{ mt: 1 }}>FAQs</Typography>
              <Typography variant="body2" color="text.secondary">
                Quick answers to common questions below.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* FAQs */}
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          Frequently asked questions
        </Typography>
        <Box sx={{ mb: 5 }}>
          {FAQS.map((f) => (
            <Accordion key={f.q} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>{f.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">{f.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Contact form */}
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          Send us a message
        </Typography>
        <Card variant="outlined">
          <CardContent>
            <Stack component="form" spacing={2} onSubmit={handleSubmit}>
              <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
              <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth required />
              <TextField
                label="How can we help?"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                multiline
                minRows={4}
                fullWidth
                required
              />
              <Button type="submit" variant="contained" size="large">Send message</Button>
            </Stack>
          </CardContent>
        </Card>

        <Divider sx={{ my: 5 }} />
        <Stack direction="row" spacing={2}>
          <Button component={Link} to="/terms" variant="outlined">Terms</Button>
          <Button component={Link} to="/" variant="contained">Home</Button>
        </Stack>
      </Container>
    </Box>
  );
}
